/**
 * Sink for routing-lifecycle telemetry. The adapter + controller
 * call `report(event)`; production wires it to whatever analytics
 * backend the host accepts (when foundation-frontend grows an
 * `@mammoth/analytics` package, route through that).
 *
 * Event names + property names + value enums are part of the
 * cross-platform contract documented in
 * `wiki/01-products/metro-routing-events.md`. Renaming any of them
 * requires a coordinated iOS + Android + Web PR pair.
 *
 * Today web has no foundation analytics package — the default
 * implementation is a no-op. A console-logger fallback ships under
 * `consoleRoutingTelemetryReporter` so dev builds can verify
 * event flow without wiring a real backend.
 */

/** Per-route source tag derived client-side from which adapter arm
 *  produced the route. Closed enum — matches the wire contract on
 *  iOS / Android byte-for-byte. */
export type RoutingRouteSource = 'tfl' | 'offline' | 'cache'

/** Aggregate top-level source attributed to a `route_resolve_succeeded`
 *  emission. `mixed` is emitted when the result blends online and
 *  offline arms (online walk-only-only + offline pure-transit).
 *  `cache` short-circuits before either arm runs. */
export type RoutingAggregateSource = 'tfl' | 'offline' | 'cache' | 'mixed'

/** Error taxonomy for `route_resolve_failed`. Adapter maps host-native
 *  `Error` instances onto this closed enum at emit time so the wire
 *  values stay identical across iOS / Android / Web. */
export type RoutingErrorKind =
  | 'no_path'
  | 'same_origin'
  | 'network'
  | 'timeout'
  | 'http_4xx'
  | 'http_5xx'
  | 'decode'
  | 'other'

export type RoutingTelemetryEvent =
  | {
      readonly kind: 'resolveStarted'
      readonly fromStation: string
      readonly toStation: string
      readonly accessibilityPref?: string
      readonly withDepartAt: boolean
      readonly withArriveAt: boolean
    }
  | {
      readonly kind: 'resolveSucceeded'
      readonly fromStation: string
      readonly toStation: string
      readonly source: RoutingAggregateSource
      readonly routeCount: number
      readonly onlineCount: number
      readonly offlineCount: number
      readonly walkingOnlyCount: number
      readonly hybridCount: number
      readonly pureTransitCount: number
      readonly durationMs: number
      readonly cacheHit: boolean
      readonly onlineUsedFallback: boolean
    }
  | {
      readonly kind: 'resolveFailed'
      readonly fromStation: string
      readonly toStation: string
      readonly errorKind: RoutingErrorKind
      readonly durationMs: number
    }
  | {
      readonly kind: 'variantTapped'
      readonly fromStation: string
      readonly toStation: string
      readonly variantIndex: number
      readonly variantTotal: number
      readonly routeSource: RoutingRouteSource
    }
  | {
      readonly kind: 'cardDismissed'
      readonly fromStation: string
      readonly toStation: string
      readonly routeTotal: number
      readonly viewedVariantIndex: number
    }

export type RoutingTelemetryReporter = (event: RoutingTelemetryEvent) => void

/**
 * Production-default no-op. Used when the host hasn't wired analytics
 * yet, or when the user has not consented to the analytics category.
 * Mirrors `NoopRoutingTelemetryReporter` shape on the other platforms.
 */
export const noopRoutingTelemetryReporter: RoutingTelemetryReporter = () => {
  // no-op
}

/**
 * Wire-format encoder. Translates the union event into the
 * `(name, parameters)` shape used by every host analytics backend
 * (`route_resolve_*`, snake_case parameter names). Pure function —
 * exposed so the eventual `@mammoth/analytics` integration can call
 * it without depending on a vendor SDK.
 *
 * Stays in sync with iOS `MetroRoutingTelemetryReporter._encode` and
 * Android `MetroRoutingTelemetryReporter.encode` byte-for-byte.
 */
export function encodeRoutingTelemetryEvent(event: RoutingTelemetryEvent): {
  readonly name: string
  readonly parameters: Readonly<Record<string, string | number | boolean>>
} {
  switch (event.kind) {
    case 'resolveStarted': {
      const params: Record<string, string | number | boolean> = {
        from_station: event.fromStation,
        to_station: event.toStation,
        with_depart_at: event.withDepartAt,
        with_arrive_at: event.withArriveAt,
      }
      if (event.accessibilityPref !== undefined && event.accessibilityPref.length > 0) {
        params.accessibility_pref = event.accessibilityPref
      }
      return { name: 'route_resolve_started', parameters: params }
    }
    case 'resolveSucceeded':
      return {
        name: 'route_resolve_succeeded',
        parameters: {
          from_station: event.fromStation,
          to_station: event.toStation,
          source: event.source,
          route_count: event.routeCount,
          online_count: event.onlineCount,
          offline_count: event.offlineCount,
          walking_only_count: event.walkingOnlyCount,
          hybrid_count: event.hybridCount,
          pure_transit_count: event.pureTransitCount,
          duration_ms: event.durationMs,
          cache_hit: event.cacheHit,
          online_used_fallback: event.onlineUsedFallback,
        },
      }
    case 'resolveFailed':
      return {
        name: 'route_resolve_failed',
        parameters: {
          from_station: event.fromStation,
          to_station: event.toStation,
          error_kind: event.errorKind,
          duration_ms: event.durationMs,
        },
      }
    case 'variantTapped':
      return {
        name: 'route_variant_tapped',
        parameters: {
          from_station: event.fromStation,
          to_station: event.toStation,
          variant_index: event.variantIndex,
          variant_total: event.variantTotal,
          route_source: event.routeSource,
        },
      }
    case 'cardDismissed':
      return {
        name: 'route_card_dismissed',
        parameters: {
          from_station: event.fromStation,
          to_station: event.toStation,
          route_total: event.routeTotal,
          viewed_variant_index: event.viewedVariantIndex,
        },
      }
  }
}

/**
 * Dev-only console reporter. Logs the encoded `(name, parameters)`
 * tuple so dev builds can sanity-check the event stream without a
 * real analytics backend. Production should never construct this.
 */
export function consoleRoutingTelemetryReporter(event: RoutingTelemetryEvent): void {
  if (process.env.NODE_ENV === 'production') return
  const { name, parameters } = encodeRoutingTelemetryEvent(event)

  console.info('[routing-telemetry]', name, parameters)
}
